import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode
} from 'react';
import {
  McpChatOnboardingContext,
  type McpChatOnboardingContextValue
} from './mcpChatOnboardingContextState';
import { useMcpChatOnboardingGate } from './useMcpChatOnboardingGate';

export function McpChatOnboardingProvider({ children }: { children: ReactNode }) {
  const gate = useMcpChatOnboardingGate();
  const needsOnboarding = !gate.isReady;
  const [modalOpen, setModalOpen] = useState(false);

  const requestOnboarding = useCallback(() => {
    if (!gate.isReady) {
      setModalOpen(true);
    }
  }, [gate.isReady]);

  const dismissOnboarding = useCallback(() => {
    setModalOpen(false);
  }, []);

  const assertChatAllowed = useCallback(() => {
    if (!gate.isReady) {
      requestOnboarding();
      return false;
    }
    return true;
  }, [gate.isReady, requestOnboarding]);

  useEffect(() => {
    if (!needsOnboarding) {
      setModalOpen(false);
    }
  }, [needsOnboarding]);

  const value = useMemo(
    () => ({
      gate,
      needsOnboarding,
      modalOpen,
      requestOnboarding,
      dismissOnboarding,
      assertChatAllowed
    }),
    [
      assertChatAllowed,
      dismissOnboarding,
      gate,
      modalOpen,
      needsOnboarding,
      requestOnboarding
    ]
  );

  if (gate.isLoading && !gate.isReady) {
    return null;
  }

  return (
    <McpChatOnboardingContext.Provider value={value}>
      {children}
    </McpChatOnboardingContext.Provider>
  );
}

export function useMcpChatOnboarding(): McpChatOnboardingContextValue {
  const ctx = useContext(McpChatOnboardingContext);
  if (!ctx) {
    throw new Error(
      'useMcpChatOnboarding must be used within McpChatOnboardingProvider'
    );
  }
  return ctx;
}

export function useOptionalMcpChatOnboarding(): McpChatOnboardingContextValue | null {
  return useContext(McpChatOnboardingContext);
}
